/**
 * Ряд карточек показателей (redesign по макету «Цифровой паспорт»):
 * название + бейдж тренда, иконка + крупное значение с единицей измерения.
 *
 * Состав: общая численность населения (`/public/general`), кликабельные
 * публикации (медиамониторинг, считает страница), ИФО инвестиций и ИФО
 * промышленности (API пока не отдаёт — карточки базовые).
 *
 * Карточки видимы всегда: без данных рендерится «базовый» вид — название
 * и иконка. Значения приходят снаружи через `values` — компонент в API
 * не ходит.
 */
import type { ComponentType } from 'react';
import {
  HardHat,
  MousePointerClick,
  TrendingUp,
  UsersRound,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * «Рука с деньгами» для «ИФО инвестиций» (замечание дизайн-ревью):
 * ладонь из lucide hand-coins + одна крупная монета с ₸ — читаемее
 * двух мелких монет штатной иконки. Стиль lucide: stroke 2, round caps.
 */
const HandMoneyIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" />
    <path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
    <path d="m2 16 6 6" />
    <circle cx="15" cy="6.5" r="4.5" />
    <path d="M13 5h4" />
    <path d="M15 5v4" />
  </svg>
);

export type SerIndicatorKey =
  | 'population'
  | 'clickablePublications'
  | 'ifoInvestments'
  | 'ifoIndustry';

export interface SerIndicatorValue {
  value: number | null;
  /** Изменение к прошлому периоду — бейдж в правом углу. */
  trend: number | null;
}

/** kind определяет формат значения: число с единицей или процент. */
const INDICATORS: Array<{
  key: SerIndicatorKey;
  icon: ComponentType<{ className?: string }>;
  kind: 'people' | 'units' | 'percent';
}> = [
  { key: 'population', icon: UsersRound, kind: 'people' },
  // Иконки по референсу дизайн-ревью: клик по публикации — курсор
  // с кликом, инвестиции — рука с деньгами.
  { key: 'clickablePublications', icon: MousePointerClick, kind: 'units' },
  { key: 'ifoInvestments', icon: HandMoneyIcon, kind: 'percent' },
  { key: 'ifoIndustry', icon: HardHat, kind: 'percent' },
];

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);
/** Тренд в бейдже — с точкой, как в макете («+1.8»). */
const fmtTrend = (v: number): string => `${v > 0 ? '+' : ''}${v}`;

interface Props {
  /** Значения по ключам показателей; нет значения → базовая карточка. */
  values?: Partial<Record<SerIndicatorKey, SerIndicatorValue>>;
}

export const SerIndicatorCards = ({ values = {} }: Props) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-3.5 xl:grid-cols-4" data-testid="ser-cards">
      {INDICATORS.map(({ key, icon: Icon, kind }) => {
        const value = values[key];
        return (
          <div
            key={key}
            data-testid={`ser-card-${key}`}
            // flex-col + mt-auto у ряда значения: заголовки бывают в одну
            // и в две строки — числа всё равно на одной линии во всех
            // карточках ряда (grid тянет карточки до одной высоты).
            className="flex min-h-[7.25rem] flex-col rounded-2xl border border-surface-border bg-gradient-to-b from-surface to-surface-page p-5 shadow-card"
          >
            {/* flex-wrap: на узкой карточке чип уходит под заголовок,
                а не вылезает за край. */}
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="min-w-0 text-sm font-extrabold leading-snug">
                {t(`digitalPassport.ser.${key}`)}
              </h3>
              {value?.trend != null && (
                <span
                  data-testid={`ser-card-${key}-trend`}
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-brand-100 bg-surface px-3 py-1 text-sm font-bold text-brand-600"
                >
                  <TrendingUp className="size-4" />
                  {fmtTrend(value.trend)}
                </span>
              )}
            </div>

            <div className="mt-auto flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-3">
              <Icon className="size-7 shrink-0 self-center text-brand-600" />
              {value?.value != null && (
                <>
                  <span
                    data-testid={`ser-card-${key}-value`}
                    className="text-3xl font-extrabold tabular-nums leading-tight text-chart-indigo"
                  >
                    {kind === 'percent' ? `${fmt1(value.value)}%` : fmt(value.value)}
                  </span>
                  {kind === 'people' && (
                    <span className="text-sm text-text-muted">
                      {t('units.people')}
                    </span>
                  )}
                  {kind === 'units' && (
                    <span className="text-sm text-text-muted">
                      {t('digitalPassport.ser.unitShort')}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
